import { Book } from "@/types/book";
import { Play, Plus, Check } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useBookImageRefresh } from "@/hooks/use-book-cache-invalidation";
import { useAuth } from "@/components/AuthProvider";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { AuthModal } from "@/components/AuthModal";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useStableAvatar } from "@/hooks/use-stable-avatar";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { navigateToBook } from "@/utils/reading-navigation";
import { useEnhancedReadingList } from "@/hooks/use-enhanced-reading-list";

// Detect Safari for alternative styling (blur causes GPU texture eviction)
const isSafari = typeof navigator !== 'undefined' && 
  (/^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
   (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream));

interface FreeBooksSectionProps {
  books: Book[];
  onBookClick: (book: Book) => void;
  isLoading?: boolean;
}

export function FreeBooksSection({ books, onBookClick, isLoading }: FreeBooksSectionProps) {
  const [refreshKey, setRefreshKey] = useState(0);
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const { isAuthModalOpen, openAuthModal, closeAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const { addToReadingList, removeFromReadingList, readingListBookIds } = useEnhancedReadingList();
  

  // Get first free book
  const book = books.length > 0 ? books[0] : null;

  // Fetch page_images for tablet/desktop background
  const { data: pageImages } = useQuery({
    queryKey: ['book-page-images', book?.id],
    queryFn: async () => {
      if (!book?.id) return null;
      const { data, error } = await supabase
        .from('books')
        .select('page_images')
        .eq('id', book.id)
        .single();
      if (error) {
        console.error('Error fetching page images:', error);
        return null;
      }
      return data?.page_images as Array<{ pageName: string; url: string }> | null;
    },
    enabled: !!book?.id,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  // Check if user has reading progress for this book  
  const { data: readingProgress } = useQuery({
    queryKey: ['reading-progress', session?.user?.id, currentProfile?.id, book?.id],
    queryFn: async () => {
      if (!session?.user?.id || !currentProfile?.id || !book?.id) return null;
      const { data, error } = await supabase
        .from('reading_sessions')
        .select('current_page')
        .eq('user_id', session.user.id)
        .eq('profile_id', currentProfile.id)
        .eq('book_id', book.id)
        .order('end_time', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error('Error fetching reading progress:', error);
        return null;
      }
      return data;
    },
    enabled: !!session?.user?.id && !!currentProfile?.id && !!book?.id,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  // Determine if user has started reading this book
  const hasStartedReading = readingProgress && readingProgress.current_page > 0;
  const buttonText = hasStartedReading ? "Resume" : "Read Now";

  const handleReadNow = () => {
    if (!book) return;
    if (!session) {
      openAuthModal("Please sign in to read this book");
    } else if (hasStartedReading) {
      navigateToBook(Number(book.id), navigate, { currentPage: readingProgress?.current_page });
    } else {
      onBookClick(book);
    }
  };

  const handleAuthSuccess = () => {
    closeAuthModal();
    if (book) {
      if (hasStartedReading) {
        navigateToBook(Number(book.id), navigate, { currentPage: readingProgress?.current_page });
      } else {
        onBookClick(book);
      }
    }
  };

  // Listen for explicit image update events for this book and refresh only then
  useBookImageRefresh(book?.id ?? 0, () => setRefreshKey(k => k + 1));

  // Refresh key follows cache_version to keep URLs stable
  useEffect(() => {
    if (book?.cache_version != null) {
      setRefreshKey(Number(book.cache_version));
    } else if (book?.id != null) {
      setRefreshKey(Number(book.id));
    } else {
      setRefreshKey(0);
    }
  }, [book?.id, book?.cache_version]);

  // Create stable image URL with optional cache-busting
  const getCacheBustedImageUrl = (url: string | null) => {
    if (!url) return '';
    try {
      const base = url.startsWith('http') ? undefined : window.location.origin;
      const imageUrl = new URL(url, base);
      if (book?.cache_version != null) {
        imageUrl.searchParams.set('v', String(book.cache_version));
      }
      if (refreshKey) {
        imageUrl.searchParams.set('t', String(refreshKey));
      }
      return imageUrl.toString();
    } catch {
      const params: string[] = [];
      if (refreshKey) params.push(`t=${refreshKey}`);
      if (book?.cache_version != null) params.push(`v=${book.cache_version}`);
      return url + (url.includes('?') ? '&' : '?') + params.join('&');
    }
  };

  if (isLoading) {
    return (
      <div className="relative w-full min-h-[400px] md:min-h-[500px]">
        <div 
          className="relative w-full overflow-hidden shadow-2xl rounded-2xl border border-white/20 animate-pulse bg-black/30"
          style={{ 
            height: '60vh', 
            minHeight: '400px', 
            maxHeight: '550px',
          }}
        />
      </div>
    );
  }

  if (!book || books.length === 0) {
    return null;
  }

  const imageUrl = getCacheBustedImageUrl(book.cover_image_url);

  // Get first page image for tablet/desktop (landscape aspect ratio)
  const getFirstPageImage = (): string | null => {
    if (!pageImages || !Array.isArray(pageImages) || pageImages.length === 0) return null;
    
    // Sort by page number and get first story page (skip cover/info pages)
    const sortedPages = [...pageImages]
      .filter(p => p.pageName?.startsWith('page-'))
      .sort((a, b) => {
        const numA = parseInt(a.pageName?.replace('page-', '') || '0');
        const numB = parseInt(b.pageName?.replace('page-', '') || '0');
        return numA - numB;
      });
    
    return sortedPages.length > 0 ? sortedPages[0].url : null;
  };

  const pageImageUrl = getCacheBustedImageUrl(getFirstPageImage());

  return (
    <>
      <div className="relative w-full min-h-[400px] md:min-h-[500px]">

        {/* Hero Card - matching NetflixHero style */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative w-full overflow-hidden shadow-2xl rounded-2xl border border-white/20 z-10"
          style={{ 
            height: '60vh', 
            minHeight: '400px', 
            maxHeight: '550px',
          }}
        >
          {/* Glow effect behind book cover */}
          <div 
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              filter: 'blur(60px) saturate(1.5)',
              opacity: 0.8,
              transform: 'scale(1.2)',
            }}
          />
          
          {/* Cover Image - Mobile (portrait) */}
          <img
            src={imageUrl || '/placeholder.svg'}
            alt={book.title}
            className="absolute inset-0 w-full h-full object-cover bg-top z-[1] md:hidden"
          />
          
          {/* Page Image - Tablet/Desktop (landscape) */}
          <img
            src={pageImageUrl || imageUrl || '/placeholder.svg'}
            alt={book.title}
            className="absolute inset-0 w-full h-full object-cover object-center z-[1] hidden md:block"
          />
          
          {/* BuxBox Logo Badge */}
          <div className="absolute top-4 left-4 z-10">
            <div className="w-10 h-10 rounded-lg bg-black/90 flex items-center justify-center border border-white/10">
              <img 
                src="/logo.png" 
                alt="BuxBox" 
                className="w-6 h-6 object-contain"
              />
            </div>
          </div>

          {/* "Free" badge */}
          <div className="absolute top-4 right-4 z-10">
            <div className="px-3 py-1.5 rounded-lg bg-green-600 text-white font-apple font-semibold text-xs border border-green-500/30">
              FREE
            </div>
          </div>
          
          {/* Bottom dark gradient - solid for performance */}
          <div 
            className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black via-black/90 to-transparent z-[2]"
          />
          
          {/* Bottom gradient overlay */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent z-[3]" />
          
          {/* Content overlay - center aligned */}
          <div className="absolute bottom-0 left-0 right-0 p-6 space-y-1 text-center z-[4]">
            {/* Title - only show if 4 words or less */}
            {book.title.split(' ').length <= 4 && (
              <h2 className="text-2xl sm:text-3xl font-apple font-bold text-white tracking-tight leading-tight">
                {book.title}
              </h2>
            )}
            
            {/* Subtitle */}
            <p className="text-white/70 font-apple text-sm">
              {hasStartedReading 
                ? `Continue where you left off` 
                : 'Start reading for free'}
            </p>
            
            {/* Buttons */}
            <div className="flex gap-2 pt-2 max-w-xs mx-auto">
              <button
                onClick={handleReadNow}
                className="flex-1 flex items-center justify-center gap-1.5 bg-white text-black font-apple font-semibold py-2.5 px-4 rounded transition-all hover:bg-white/90 active:scale-[0.98]"
              >
                <Play className="w-4 h-4 fill-current" />
                {buttonText}
              </button>
              <button
                onClick={() => {
                  if (!session) {
                    openAuthModal("Please sign in to add to your list");
                    return;
                  }
                  if (!book) return;
                  if (readingListBookIds.includes(book.id)) {
                    removeFromReadingList(book.id);
                  } else {
                    addToReadingList(book.id);
                  }
                }}
                className="flex-1 flex items-center justify-center gap-1.5 bg-white/25 text-white font-apple font-semibold py-2.5 px-4 rounded border border-white/30 transition-all hover:bg-white/35 active:scale-[0.98]"
              >
                {book && readingListBookIds.includes(book.id) ? (
                  <>
                    <Check className="w-4 h-4" />
                    My List
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    My List
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
      
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={closeAuthModal} 
        onAuthSuccess={handleAuthSuccess} 
        context="Please sign in to read this book" 
      />
    </>
  );
}
